import React, { useCallback, useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../config/firebaseConfig";
import { getUserRoles, hasPermission, PERMISSIONS } from "../utils/roleAccess";

export default function ViewPayments() {
  const [statusFilter, setStatusFilter] = useState("submitted");
  const [payments, setPayments] = useState([]);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [canManagePayments, setCanManagePayments] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const auth = getAuth();

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "received_payment"));
      const data = querySnapshot.docs.map((docSnap) => ({
        ...docSnap.data(),
        id: docSnap.id,
      }));
      setPayments(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (
          userDoc.exists() &&
          hasPermission(userDoc.data(), PERMISSIONS.VIEW_PAYMENTS)
        ) {
          const roles = getUserRoles(userDoc.data());
          setIsAuthorized(true);
          setCanManagePayments(roles.includes("admin"));
          fetchPayments();
          return;
        }
      }
      setIsAuthorized(false);
      setCanManagePayments(false);
    });

    return () => unsubscribe();
  }, [auth, fetchPayments]);

  const updateStatus = async (id, newStatus) => {
    setLoading(true);
    try {
      const currentUser = auth.currentUser;
      const docRef = doc(db, "received_payment", id);

      await updateDoc(docRef, {
        status: newStatus,
        updatedAt: serverTimestamp(),
        updatedByUserEmail: currentUser?.email || "Unknown",
        updatedByUserUID: currentUser?.uid || "Unknown",
      });

      await fetchPayments();
    } catch (err) {
      console.error("Error updating status:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500 text-lg">Access denied. Admin only.</p>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-4xl mx-auto relative">
      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white px-6 py-4 rounded-lg shadow-md">
            <p className="text-gray-700 font-semibold">Processing...</p>
          </div>
        </div>
      )}

      <div className="flex justify-around mb-6">
        {["submitted", "rejected", "approved"].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 rounded-lg font-semibold text-white transition ${
              statusFilter === status
                ? status === "submitted"
                  ? "bg-blue-800"
                  : status === "rejected"
                    ? "bg-red-600"
                    : "bg-green-600"
                : "bg-gray-400"
            }`}
          >
            {status.toUpperCase()}
          </button>
        ))}
      </div>

      {!canManagePayments && (
        <p className="mb-4 rounded-lg bg-blue-50 p-3 text-sm text-blue-800">
          Payments are shown in read-only mode for Principal accounts.
        </p>
      )}

      <div className="space-y-4">
        {payments
          .filter(
            (payment) =>
              payment.status?.toLowerCase() === statusFilter.toLowerCase()
          )
          .map((payment) => (
            <div
              key={payment.id}
              className="bg-white rounded-2xl shadow-md p-4 flex flex-col md:flex-row md:items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <div
                  onClick={() => setSelectedImage(payment.fileUrl)}
                  className="w-20 h-20 bg-gray-100 rounded-xl flex items-center justify-center overflow-hidden cursor-pointer"
                >
                  <img
                    src={payment.fileUrl}
                    alt="Payment proof"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div>
                  <p className="font-bold text-gray-800">
                    Name: {payment.childName}
                  </p>
                  <p className="text-sm text-gray-600">
                    Class: {payment.childClass} {payment.section}
                  </p>
                  <p className="text-sm text-gray-600">
                    Roll No: {payment.rollNumber}
                  </p>
                  <p className="text-xs text-gray-500 italic">
                    Status: {payment.status}
                  </p>
                  <p className="text-xs text-gray-400">
                    Updated by: {payment.updatedByUserEmail || "-"}
                  </p>
                </div>
              </div>

              {canManagePayments && <div className="flex gap-2 mt-3 md:mt-0">
                <button
                  onClick={() => updateStatus(payment.id, "Approved")}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-1 rounded-lg shadow"
                >
                  Approve
                </button>
                <button
                  onClick={() => updateStatus(payment.id, "Rejected")}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-1 rounded-lg shadow"
                >
                  Reject
                </button>
              </div>}
            </div>
          ))}
      </div>

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
          onClick={() => setSelectedImage(null)}
        >
          <img
            src={selectedImage}
            alt="Full size proof"
            className="max-w-full max-h-full rounded-xl shadow-lg"
          />
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 bg-white text-black px-3 py-1 rounded-lg shadow"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
